import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import BulkUploadDialog from "@/components/BulkUploadDialog";

const TYPES = ["RAW", "PACKING", "CONSUMABLE", "SEMI_FINISHED", "FINISHED", "TRADING"];

export default function Items() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({});

  function blank() {
    return { sku: "", name: "", item_type: "RAW", unit: "unit", reorder_level: 0, standard_cost: 0, sale_price: 0, hsn_code: "", vendor_id: "", vendor_name: "" };
  }

  const load = async () => {
    const [it, v] = await Promise.all([
      api.get("/inventory/items"),
      api.get("/org/vendors").catch(() => ({ data: [] })),
    ]);
    setItems(it.data); setVendors(v.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.vendor_name && !form.vendor_id) { toast.error("Vendor is required"); return; }
    try {
      await api.post("/inventory/items", {
        ...form,
        reorder_level: Number(form.reorder_level),
        standard_cost: Number(form.standard_cost),
        sale_price: Number(form.sale_price),
      });
      toast.success("Item added"); setOpen(false); setForm(blank()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const beginEdit = (i) => {
    setEdit(i.id);
    setEditForm({
      sku: i.sku, name: i.name, item_type: i.item_type, unit: i.unit,
      vendor_name: i.vendor_name || "",
      standard_cost: i.standard_cost || 0,
      sale_price: i.sale_price || i.standard_cost || 0,
      reorder_level: i.reorder_level || 0,
      hsn_code: i.hsn_code || "",
    });
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/inventory/items/${edit}`, {
        name: editForm.name || null,
        standard_cost: Number(editForm.standard_cost) || 0,
        sale_price: Number(editForm.sale_price) || 0,
        reorder_level: Number(editForm.reorder_level) || 0,
        vendor_name: editForm.vendor_name || null,
        hsn_code: editForm.hsn_code || null,
        unit: editForm.unit || null,
      });
      toast.success("Item updated"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="items-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Item master</h1>
          <p className="text-muted-foreground">Raw materials, packing, finished goods — with vendor & editable pricing.</p>
        </div>
        <div className="flex items-center gap-2">
          <BulkUploadDialog
            title="Bulk upload items"
            templateUrl="/inventory/items/template"
            uploadUrl="/inventory/items/bulk"
            payloadKey="items"
            testId="bulk-items"
            onSuccess={load}
          />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-item-btn"><Plus className="w-4 h-4 mr-1" /> New item</Button></DialogTrigger>
            <DialogContent data-testid="item-dialog">
            <DialogHeader><DialogTitle>New item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="item-sku" /></div>
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                    <SelectTrigger data-testid="item-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" /></div>
              <div className="space-y-2">
                <Label>Vendor <span className="text-red-500">*</span></Label>
                {vendors.length > 0 ? (
                  <Select value={form.vendor_id || ""} onValueChange={(v) => {
                    const sel = vendors.find((x) => x.id === v);
                    setForm({ ...form, vendor_id: v, vendor_name: sel?.name || "" });
                  }}>
                    <SelectTrigger data-testid="item-vendor"><SelectValue placeholder="Pick vendor" /></SelectTrigger>
                    <SelectContent>
                      {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-xs text-amber-700 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">No vendors yet — add one in <b>Vendors</b> first.</div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
                <div className="space-y-2"><Label>HSN code</Label><Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} /></div>
                <div className="space-y-2"><Label>Price (₹)</Label><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="item-sale" /></div>
              </div>
              <Button onClick={submit} data-testid="item-save">Save item</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>HSN</TableHead>
              <TableHead>Price</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`item-row-${i.sku}`}>
                <TableCell className="font-mono text-xs">{i.sku}</TableCell>
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{i.item_type}</Badge></TableCell>
                <TableCell className="text-xs">{i.vendor_name || "—"}</TableCell>
                <TableCell>{i.unit}</TableCell>
                <TableCell className="font-mono text-xs">{i.hsn_code || "—"}</TableCell>
                <TableCell className="font-mono">₹{Number(i.sale_price || i.standard_cost || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => beginEdit(i)} data-testid={`item-edit-${i.sku}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No items yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="item-edit-dialog">
          <DialogHeader><DialogTitle>Edit item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>SKU</Label><Input value={editForm.sku || ""} disabled /></div>
              <div className="space-y-2"><Label>Type</Label><Input value={editForm.item_type || ""} disabled /></div>
            </div>
            <div className="space-y-2"><Label>Name</Label><Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="item-edit-name" /></div>
            <div className="space-y-2"><Label>Vendor name</Label><Input value={editForm.vendor_name || ""} onChange={(e) => setEditForm({ ...editForm, vendor_name: e.target.value })} data-testid="item-edit-vendor" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Price (₹)</Label><Input type="number" value={editForm.sale_price || 0} onChange={(e) => setEditForm({ ...editForm, sale_price: e.target.value })} data-testid="item-edit-sale" /></div>
              <div className="space-y-2"><Label>Unit</Label><Input value={editForm.unit || ""} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>HSN code</Label><Input value={editForm.hsn_code || ""} onChange={(e) => setEditForm({ ...editForm, hsn_code: e.target.value })} data-testid="item-edit-hsn" /></div>
            <Button onClick={saveEdit} data-testid="item-edit-save">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
